import fancyMapStyles from "@/components/Molecules/MapComponent/mapStyles.json";

export type MapIconAsset = {
	url: string;
	alt?: string;
	width?: number;
	height?: number;
};

export type MapAccentColor = {
	hexCode?: string;
	hex?: string;
	color?: { hex?: string; cssRgb?: string };
};

/** Dominant accent in mapStyles.json — used when preset is styled but no CMS accent is set. */
export const DEFAULT_STYLED_MAP_ACCENT = "#af5b2d";

/** Default pin / drawer accent when CMS map accent is not set. */
export const DEFAULT_PIN_ACCENT = "#CE0F69";

export function isStyledMapPreset(preset?: string | null) {
	return preset === "minimal" || preset === "brand";
}

export function getMapAccentColor(color?: MapAccentColor | null) {
	if (!color) return undefined;

	const raw =
		color.hexCode ??
		color.hex ??
		color.color?.hex ??
		color.color?.cssRgb;

	if (!raw?.trim()) return undefined;

	const trimmed = raw.trim();
	if (trimmed.startsWith("#") || trimmed.startsWith("rgb")) {
		return trimmed;
	}

	return `#${trimmed.replace(/^#/, "")}`;
}

export function resolveSectionFontColor(color?: string | null) {
	if (!color?.trim()) return undefined;
	const trimmed = color.trim();
	return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

/** Pin / drawer accent — CMS map accent, then section font color, then brand pin default. */
export function resolveMapPinAccent(
	mapAccentColor?: MapAccentColor | null,
	sectionFontColor?: string | null
) {
	return (
		getMapAccentColor(mapAccentColor) ??
		resolveSectionFontColor(sectionFontColor) ??
		DEFAULT_PIN_ACCENT
	);
}

/** Map tile styling — pin accent, then styled-preset fallback (mirrors Google fancyMapStyles). */
export function resolveMapStyleAccent(
	mapAccentColor?: MapAccentColor | null,
	sectionFontColor?: string | null,
	mapStylePreset?: string | null
) {
	const pinAccent = getMapAccentColor(mapAccentColor) ?? resolveSectionFontColor(sectionFontColor);
	if (pinAccent) return pinAccent;

	if (isStyledMapPreset(mapStylePreset)) {
		return DEFAULT_STYLED_MAP_ACCENT;
	}

	return undefined;
}

export function getGoogleMapStyles(
	preset?: string | null,
	accentColor?: string | null
) {
	if (preset !== "minimal" && preset !== "brand") {
		return undefined;
	}

	if (!accentColor) {
		return fancyMapStyles;
	}

	const accent = accentColor.startsWith("#")
		? accentColor
		: `#${accentColor.replace("#", "")}`;

	return fancyMapStyles.map((rule) => ({
		...rule,
		stylers: rule.stylers?.map((styler) => {
			if (!("color" in styler) || typeof styler.color !== "string") {
				return styler;
			}

			const normalized = styler.color.replace("#", "").toLowerCase();
			const brandColors = new Set([
				"af5b2d",
				"aa2c2c",
				"c14141",
				"884848",
			]);

			if (!brandColors.has(normalized)) return styler;

			return { ...styler, color: accent };
		}),
	}));
}

export function getOsmTileLayer() {
	return {
		url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
		attribution:
			'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
	};
}

export function getMapIconSize(mapIcon?: MapIconAsset | null, fallback = 48) {
	return {
		width: mapIcon?.width ?? fallback,
		height: mapIcon?.height ?? fallback,
	};
}

export function createAccentPinMarkerHtml(
	mapIcon?: MapIconAsset | null,
	accentColor?: string
) {
	const color = accentColor ?? DEFAULT_PIN_ACCENT;
	const { width, height } = getMapIconSize(mapIcon ?? undefined, mapIcon?.url ? 56 : 40);

	if (mapIcon?.url) {
		return `<div aria-hidden="true" style="width:${width}px;height:${height}px;background-color:${color};-webkit-mask:url('${mapIcon.url}') center/contain no-repeat;mask:url('${mapIcon.url}') center/contain no-repeat;"></div>`;
	}

	return createAccentPinSvg(color);
}

export function getAccentPinDimensions(mapIcon?: MapIconAsset | null) {
	return getMapIconSize(mapIcon ?? undefined, mapIcon?.url ? 56 : 40);
}

export const LOCATION_MAP_ZOOM = 15;

export function getLocationMapHeight(width: number) {
	return width < 450 ? "20vh" : "600px";
}

export function createAccentPinSvg(accentColor?: string) {
	const color = accentColor ?? DEFAULT_PIN_ACCENT;

	return `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
    <path fill="${color}" d="M20 0C11.716 0 5 6.716 5 15c0 10.5 15 25 15 25s15-14.5 15-25C35 6.716 28.284 0 20 0z"/>
    <circle cx="20" cy="15" r="6" fill="#ffffff"/>
  </svg>`;
}
